import questionnairesService from "@/services/questionnairesService";
import catalogsService from "@/services/catalogsService";

const resolveSortOrder = (item) => {
    const sortOrder = Number(item?.sortOrder);
    if (Number.isFinite(sortOrder)) {
        return sortOrder;
    }
    const id = Number(item?.id);
    return Number.isFinite(id) ? id : Number.MAX_SAFE_INTEGER;
};

const sortBySortOrder = (items = []) => {
    if (!Array.isArray(items)) {
        return [];
    }
    return [...items].sort((a, b) => resolveSortOrder(a) - resolveSortOrder(b));
};

const sortWorkPlanHierarchy = (questionnaire) => {
    if (!questionnaire || !questionnaire.prioritization || !Array.isArray(questionnaire.prioritization.dimensions)) {
        return questionnaire;
    }

    questionnaire.prioritization.dimensions.forEach((dimension) => {
        if (!Array.isArray(dimension.blocks)) {
            return;
        }
        dimension.blocks = sortBySortOrder(dimension.blocks);
        dimension.blocks.forEach((block) => {
            if (!Array.isArray(block.elements)) {
                return;
            }
            block.elements = sortBySortOrder(block.elements);
            block.elements.forEach((element) => {
                if (!Array.isArray(element.questions)) {
                    return;
                }
                element.questions = sortBySortOrder(element.questions);
            });
        });
    });

    return questionnaire;
};

const state = {
    prioritization: null,
    questionnaire: null,
    isLoading: false,
    workPlanQuestionStatuses: [],
    workPlanQuestionFinalEvaluationStatuses: [],
    workPlanQuestionDetailTrackingLogs: [],
    workPlanQuestionDetailFinalEvaluation: null,
    workPlanQuestionDetail: null,
    workPlanAdditionalInformationTrackingLogs: [],
    workPlanAdditionalInformationFinalEvaluation: null,
    workPlanAdditionalInformation: null,
    workPlanProcessTrackingLogs: [],
    workPlanProcessFinalEvaluation: null,
    workPlanProcess: null,
    showUntracked: false
};

const getters = {
    getPrioritization: state => state.prioritization,
    getQuestionnaire: state => state.questionnaire,
    getIsLoading: state => state.isLoading,
    getWorkPlanQuestionStatuses: state => state.workPlanQuestionStatuses,
    getWorkPlanQuestionFinalEvaluationStatuses: state => state.workPlanQuestionFinalEvaluationStatuses,
    getWorkPlanQuestionDetailTrackingLogs: state => state.workPlanQuestionDetailTrackingLogs,
    getWorkPlanQuestionDetailFinalEvaluation: state => state.workPlanQuestionDetailFinalEvaluation ? state.workPlanQuestionDetailFinalEvaluation : {},
    getWorkPlanQuestionDetail: state => state.workPlanQuestionDetail,
    getWorkPlanAdditionalInformationTrackingLogs: state => state.workPlanAdditionalInformationTrackingLogs,
    getWorkPlanAdditionalInformationFinalEvaluation: state => state.workPlanAdditionalInformationFinalEvaluation ? state.workPlanAdditionalInformationFinalEvaluation : {},
    getWorkPlanAdditionalInformation: state => state.workPlanAdditionalInformation,
    getWorkPlanProcessTrackingLogs: state => state.workPlanProcessTrackingLogs,
    getWorkPlanProcessFinalEvaluation: state => state.workPlanProcessFinalEvaluation ? state.workPlanProcessFinalEvaluation : {},
    getWorkPlanProcess: state => state.workPlanProcess,
    getShowUntracked: state => state.showUntracked
};

const actions = {
    async getAllQuestionnaires({state}, filters) {
        try {            
            const {data} = await questionnairesService.getAllCompletedTable(filters);            
            return {
                data: data,
                error: false
            }
        } catch (error) {
            return {
                error: true,
                message: 'Error fetching questionnaires'
            }
        }
    },
    async getQuestionnairePrioritization({state}, id) {
        try {        
            const {data} = await questionnairesService.getQuestionnairePrioritizationByQuestionnaireId(id);
            state.prioritization = data;            
            return {
                data: data,
                error: false
            }
        } catch (error) {
            return {
                error: true,
                message: 'Error fetching prioritization'
            }
        }
    },
    async saveQuestionnairePrioritization({state}, id) {
        try {
            const {data} = await questionnairesService.saveQuestionnairePrioritizationByQuestionnaire(id, state.prioritization);
            state.prioritization = data;
            if (!data.result) {
                state.prioritization = null;
            }
            return data;
        } catch (error) {
            return {
                error: true,
                message: 'Error saving prioritization'
            }
        }
    },
    async getQuestionnaireWorkPlan({state}, id) {
        try {
            const {data} = await questionnairesService.getWorkPlanByQuestionnaireId(id);
            state.questionnaire = sortWorkPlanHierarchy(data);
            return {                
                error: false
            }
        } catch (error) {
            return {
                error: true,
                message: 'Error fetching work plan'
            }
        }
    },
    async updateWorkPlanElementTraining({state}, {workPlanElementId, trainingId}) {
        try {
            state.isLoading = true;
            const {data} = await questionnairesService.updateQuestionnaireWorkPlanElementTraining(workPlanElementId, trainingId);
            state.isLoading = false;
            return data;
        } catch (error) {
            return {
                error: true,
                message: 'Error updating work plan element training'
            }
        }
    },
    async updateWorkPlanElementTrainingText({state}, {workPlanElementId, trainingText}) {
        try {
            state.isLoading = true;
            const {data} = await questionnairesService.updateQuestionnaireWorkPlanElementTrainingText(workPlanElementId, { trainingText });
            state.isLoading = false;
            return data;
        } catch (error) {
            return {
                error: true,
                message: 'Error updating work plan element training text'
            }
        }
    },
    async updateWorkPlanQuestionDetail({state}, {questionDetailId, question}) {
        try {
            state.isLoading = true;
            const {data} = await questionnairesService.updateQuestionnaireWorkPlanQuestionDetail(questionDetailId, question);
            state.isLoading = false;
            return data;
        } catch (error) {
            return {
                error: true,
                message: 'Error updating work plan question detail'
            }
        }
    },
    async getWorkPlanQuestionStatuses({state}) {
        try {            
            const {data} = await catalogsService.getStatusesByType('workplan_question');
            state.workPlanQuestionStatuses = data.map(status => ({ value: status.id, label: status.description }));
            return {
                error: false
            }
        } catch (error) {
            return {
                error: true,
                message: 'Error fetching work plan question statuses'
            }
        }
    },
    async getWorkPlanQuestionFinalEvaluationStatuses({state}) {
        try {
            const {data} = await catalogsService.getStatusesByType('workplan_final_evaluation');
            state.workPlanQuestionFinalEvaluationStatuses = data.map(status => ({ value: status.id, label: status.description }));
            return {
                error: false
            }
        } catch (error) {
            return {
                error: true,
                message: 'Error fetching work plan final evaluation statuses'
            }
        }
    },
    async fetchStatuses({dispatch}) {        
        await dispatch('getWorkPlanQuestionStatuses');
        await dispatch('getWorkPlanQuestionFinalEvaluationStatuses');
    },
    async getQuestionDetailTrackingLogs({state}, question) {
        try {
            state.workPlanQuestionDetailTrackingLogs = [];            
            const {data} = await questionnairesService.getQuestionDetailTrackingLogs(question.questionnaireWorkPlanQuestionDetailId);
            state.workPlanQuestionDetailTrackingLogs = data;
            state.workPlanQuestionDetail = question;
            return {
                error: false
            }
        } catch (error) {
            return {
                error: true,
                message: 'Error fetching question detail tracking logs'
            }
        }
    },
    async getQuestionDetailFinalEvaluation({state}, question) {
        try {
            state.workPlanQuestionDetailFinalEvaluation = null;            
            const {data} = await questionnairesService.getQuestionDetailFinalEvaluation(question.questionnaireWorkPlanQuestionDetailId);
            state.workPlanQuestionDetailFinalEvaluation = data;
            state.workPlanQuestionDetail = question;
            return {
                error: false
            }
        } catch (error) {
            return {
                error: true,
                message: 'Error fetching question detail final evaluation'
            }
        }
    },
    async saveQuestionDetailTrackingLog({state}, {questionDetailId, questionLog}) {
        try {
            state.isLoading = true;
            const {data} = await questionnairesService.saveQuestionDetailTrackingLog(questionDetailId, questionLog);
            state.isLoading = false;
            state.workPlanQuestionDetail = null;
            return data;
        } catch (error) {
            return {
                error: true,
                message: 'Error saving question detail tracking log'
            }
        }
    },
    async saveQuestionDetailFinalEvaluation({state}, {questionDetailId, evaluation}) {
        try {
            state.isLoading = true;
            const {data} = await questionnairesService.saveQuestionDetailFinalEvaluation(questionDetailId, evaluation);
            state.isLoading = false;
            state.workPlanQuestionDetail = null;
            return data;
        } catch (error) {
            return {
                error: true,
                message: 'Error saving question detail final evaluation'
            }
        }
    },
    async downloadPdf({state}, questionnaireId) {
        try {
            state.isLoading = true;
            const response = await questionnairesService.getQuestionDetailPdf(questionnaireId);
            state.isLoading = false;
            return {
                error: false,
                data: response.data
            }
        } catch (error) {
            console.log(error);
            state.isLoading = false;
            return {
                error: true,
                message: 'Error downloading PDF'
            }
        }
    },
    async updateWorkPlanAdditionalInformation({state}, {additionalInformationId, additionalInformation}) {
        try {
            state.isLoading = true;
            const {data} = await questionnairesService.updateQuestionnaireWorkPlanAdditionalInformation(additionalInformationId, additionalInformation);
            state.isLoading = false;
            return data;
        } catch (error) {
            return {
                error: true,
                message: 'Error updating work plan additional information'
            }
        }
    },
    async getAdditionalInformationTrackingLogs({state}, information) {
        try {
            state.workPlanAdditionalInformationTrackingLogs = [];            
            const {data} = await questionnairesService.getAdditionalInformationTrackingLogs(information.id);
            state.workPlanAdditionalInformationTrackingLogs = data;
            state.workPlanAdditionalInformation = information;
            return {
                error: false
            }
        } catch (error) {
            return {
                error: true,
                message: 'Error fetching additional information tracking logs'
            }
        }
    },
    async getAdditionalInformationFinalEvaluation({state}, information) {
        try {
            state.workPlanAdditionalInformationFinalEvaluation = null;            
            const {data} = await questionnairesService.getAdditionalInformationFinalEvaluation(information.id);
            state.workPlanAdditionalInformationFinalEvaluation = data;
            state.workPlanAdditionalInformation = information;
            return {
                error: false
            }
        } catch (error) {
            return {
                error: true,
                message: 'Error fetching additional information final evaluation'
            }
        }
    },
    async saveAddtionalInformationTrackingLog({state}, {additionalInformationId, log}) {
        try {
            state.isLoading = true;
            const {data} = await questionnairesService.saveAdditionalInformationTrackingLog(additionalInformationId, log);
            state.isLoading = false;
            state.workPlanAdditionalInformation = null;
            return data;
        } catch (error) {
            return {
                error: true,
                message: 'Error saving additional information tracking log'
            }
        }
    },
    async saveAdditionalInformationFinalEvaluation({state}, {additionalInformationId, evaluation}) {
        try {
            state.isLoading = true;
            const {data} = await questionnairesService.saveAdditionalInformationFinalEvaluation(additionalInformationId, evaluation);
            state.isLoading = false;
            state.workPlanAdditionalInformation = null;
            return data;
        } catch (error) {
            return {
                error: true,
                message: 'Error saving additional information final evaluation'
            }
        }
    },
    async createWorkPlanProcess({state}, {questionnaireId, prioritizationId, payload}) {
        try {
            state.isLoading = true;
            const {data} = await questionnairesService.createWorkPlanProcess(questionnaireId, prioritizationId, payload);
            state.isLoading = false;
            return data;
        } catch (error) {
            return {
                error: true,
                message: 'Error creating work plan process'
            }
        }
    },
    async updateWorkPlanProcess({state}, {processId, payload}) {
        try {
            state.isLoading = true;
            const {data} = await questionnairesService.updateWorkPlanProcess(processId, payload);
            state.isLoading = false;
            return data;
        } catch (error) {
            return {
                error: true,
                message: 'Error updating work plan process'
            }
        }
    },
    async deleteWorkPlanProcess({state}, processId) {
        try {
            state.isLoading = true;
            const {data} = await questionnairesService.deleteWorkPlanProcess(processId);
            state.isLoading = false;
            return data;
        } catch (error) {
            return {
                error: true,
                message: 'Error deleting work plan process'
            }
        }
    },
    async getWorkPlanProcessTrackingLogs({state}, process) {
        try {
            state.workPlanProcessTrackingLogs = [];
            const {data} = await questionnairesService.getWorkPlanProcessTrackingLogs(process.id);
            state.workPlanProcessTrackingLogs = data;
            state.workPlanProcess = process;
            return { error: false };
        } catch (error) {
            return {
                error: true,
                message: 'Error fetching process tracking logs'
            }
        }
    },
    async saveWorkPlanProcessTrackingLog({state}, {processId, log}) {
        try {
            state.isLoading = true;
            const {data} = await questionnairesService.saveWorkPlanProcessTrackingLog(processId, log);
            state.isLoading = false;
            state.workPlanProcess = null;
            return data;
        } catch (error) {
            return {
                error: true,
                message: 'Error saving process tracking log'
            }
        }
    },
    async getWorkPlanProcessFinalEvaluation({state}, process) {
        try {
            state.workPlanProcessFinalEvaluation = null;
            const {data} = await questionnairesService.getWorkPlanProcessFinalEvaluation(process.id);
            state.workPlanProcessFinalEvaluation = data;
            state.workPlanProcess = process;
            return { error: false };
        } catch (error) {
            return {
                error: true,
                message: 'Error fetching process final evaluation'
            }
        }
    },
    async saveWorkPlanProcessFinalEvaluation({state}, {processId, evaluation}) {
        try {
            state.isLoading = true;
            const {data} = await questionnairesService.saveWorkPlanProcessFinalEvaluation(processId, evaluation);
            state.isLoading = false;
            state.workPlanProcess = null;
            return data;
        } catch (error) {
            return {
                error: true,
                message: 'Error saving process final evaluation'
            }
        }
    }
};

const mutations = {
    setBlockSelected(state, { dimensionId, blockId, selected }) {
        const dimension = state.prioritization.dimensions.find(d => d.id === dimensionId);
        if (dimension) {
            const block = dimension.blocks.find(b => b.id === blockId);
            if (block) {
                block.selected = selected;
            }
        }
    },
    setIsLoading(state, value) {
        state.isLoading = value;
    },
    setQuestionnaire(state, questionnaire) {
        state.questionnaire = questionnaire;
    },
    setWorkPlanQuestionDetail(state, questionDetail) {
        state.workPlanQuestionDetail = questionDetail;
    },
    setWorkPlanQuestionDetailFinalEvaluationStatus(state, status) {
        state.workPlanQuestionDetailFinalEvaluation.status = status;
    },
    setWorkPlanProcess(state, process) {
        state.workPlanProcess = process;
    },
    setShowUntracked(state, value) {
        state.showUntracked = value;
    }
};


export default {
    namespaced: true,
    state,
    getters,
    actions,
    mutations
};